/**
 * SamConversationSheet — la conversation avec Sam, en feuille (mobile) ou en
 * panneau latéral (web large).
 *
 * Parti pris : Sam n'est pas un live, c'est un outil de déblocage. L'identité
 * de Sam tient donc en une ligne d'en-tête, et tout le reste de la surface va
 * à la conversation et au composeur — comme le font les assistants de
 * référence (Meta AI, ChatGPT, Claude, Grok), où le logo de l'assistant est
 * quasi invisible.
 *
 * Layout :
 *  - En-tête  : avatar 40 px (animé UNIQUEMENT quand Sam réfléchit) + « Sam »
 *               + statut + fermer. Pas de badge LIVE : la fonctionnalité vit
 *               hors séance, et le rouge est notre couleur d'alerte.
 *  - Fil      : bulles sans avatar (conversation à deux — cf. WhatsApp),
 *               photos envoyées par l'élève, séparateurs d'activité, et une
 *               carte d'attente qui NOMME l'étape pendant l'analyse d'une
 *               photo (plusieurs secondes : trois points ne suffisent pas).
 *  - Actions  : une seule rangée de pastilles pleines, toujours montée —
 *               désactivée, jamais démontée, sinon la mise en page saute à
 *               chaque tour de parole.
 *  - Composeur: une carte (champ + micro + envoi), qui GARDE le focus après
 *               l'envoi.
 */
import React, { useContext, useEffect, useRef, useState } from "react"
import {
    View,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    Animated as RNAnimated,
    Easing,
    TextInput,
    Image,
} from "react-native"
import Animated, {
    SlideInDown,
    SlideOutDown,
    SlideInRight,
    SlideOutRight,
    FadeIn,
    FadeOut,
    useSharedValue,
    useAnimatedStyle,
    withSpring,
    runOnJS,
} from "react-native-reanimated"
import { Gesture, GestureDetector } from "react-native-gesture-handler"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import TextM from "@common/components/text/TextM"
import RichText from "@common/components/richText/RichText"
import { useKeyboardLift } from "@common/hooks/useKeyboardLift"
import { SAM_BUBBLE_ORIGIN } from "@data/constants/samConstants"
import { ChampionImageM } from "../../champions/ChampionImageM"

// Deux hauteurs, comme une feuille iOS moderne (Best Buy, Fabric, Craft,
// Matter) : la basse laisse voir le haut de l'énoncé, la haute donne de la
// place à une réponse longue. Toute la fonctionnalité consiste à aider sur CET
// exercice — l'élève doit pouvoir le relire sans fermer la conversation.
const DETENT = { default: "default", large: "large" }
const DETENT_RATIO = { default: 0.66, large: 0.93 }
// Side panel width on desktop — wide enough to read 2 message turns
// without horizontal scroll, narrow enough to keep the activity
// readable next to it.
const WEB_SIDE_PANEL_WIDTH = 420
// Below this viewport width we keep the bottom-sheet pattern even on
// web (mobile browsers, narrow tablets). Above, Sam becomes a side
// panel anchored to the right.
const WEB_SIDE_PANEL_BREAKPOINT = 900

const isWebSidePanel = (width) =>
    IS_WEB && (width ?? 0) >= WEB_SIDE_PANEL_BREAKPOINT

// 40 px, pas 70. L'en-tête d'une conversation n'a pas à redire à l'élève à
// qui il parle : il vient de le décider en ouvrant la feuille.
const SAM_AVATAR_SIZE = 40

// Au-delà, on dit à l'élève que c'est plus long que d'habitude plutôt que de
// le laisser devant une animation muette.
const ANALYSIS_REASSURE_AFTER_MS = 9000

// Fond de la feuille : un gris très légèrement bleuté, assez pâle pour rester
// neutre. Les aplats blancs (bulles de Sam, composeur) s'y détachent.
//
// OPAQUE, impérativement : avec une couleur en alpha (`sam + "0A"`), l'énoncé
// se lisait à travers la feuille et le nom de Sam se superposait au texte de
// l'activité.
const SHEET_GROUND = "#F4F7F8"

// L'avatar de l'accueil, lui, a le droit d'être grand et animé : c'est le
// seul moment où l'identité de Sam est l'information principale. Dès que la
// conversation démarre, il disparaît (cf. showIntro).
const INTRO_AVATAR_SIZE = 64

/**
 * Le statut ne s'affiche que lorsqu'il dit quelque chose que l'élève ne voit
 * pas déjà. « Sam attend », « Sam te parle », « Sam t'écoute » décrivaient
 * l'évidence sous le nom de Sam ; seule l'attente d'une réponse est une
 * information — elle explique pourquoi rien n'arrive.
 */
const statusOf = (isSamThinking) => (isSamThinking ? "Sam réfléchit…" : null)

/**
 * Avatar de Sam. Le halo ne pulse QUE pendant que Sam réfléchit : une
 * animation perpétuelle dans le champ de vision d'un élève qui lit un énoncé
 * n'est pas de la présence, c'est du bruit — et de la batterie sur mobile.
 */
const SamHaloAvatar = ({ samMood, samNudgeKey, active }) => {
    const haloAnim = useRef(new RNAnimated.Value(0)).current
    const [animKey, setAnimKey] = useState(0)

    useEffect(() => {
        if (!active) {
            haloAnim.stopAnimation(() => haloAnim.setValue(0))
            return
        }
        haloAnim.setValue(0)
        const loop = RNAnimated.loop(
            RNAnimated.sequence([
                RNAnimated.timing(haloAnim, {
                    toValue: 1,
                    duration: 900,
                    easing: Easing.out(Easing.quad),
                    useNativeDriver: true,
                }),
                RNAnimated.timing(haloAnim, {
                    toValue: 0,
                    duration: 700,
                    easing: Easing.in(Easing.quad),
                    useNativeDriver: true,
                }),
            ]),
        )
        loop.start()
        return () => loop.stop()
    }, [active])

    useEffect(() => {
        setAnimKey((k) => k + 1)
    }, [samNudgeKey])

    const haloScale = haloAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [1, 1.25],
    })
    const haloOpacity = haloAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 0.55],
    })

    return (
        <View style={styles.haloWrap}>
            <RNAnimated.View
                pointerEvents="none"
                style={[
                    styles.halo,
                    {
                        backgroundColor: theme.colors.sam,
                        opacity: haloOpacity,
                        transform: [{ scale: haloScale }],
                    },
                ]}
            />
            <View style={styles.avatarCircle}>
                <ChampionImageM
                    name="sam"
                    animated={active}
                    animationKey={`${samMood}-${animKey}`}
                    width={SAM_AVATAR_SIZE - 6}
                />
            </View>
        </View>
    )
}

/** Animated 3 dots — typing indicator. */
const TypingDots = () => {
    const dot1 = useRef(new RNAnimated.Value(0)).current
    const dot2 = useRef(new RNAnimated.Value(0)).current
    const dot3 = useRef(new RNAnimated.Value(0)).current

    useEffect(() => {
        const animateDot = (val, delay) =>
            RNAnimated.loop(
                RNAnimated.sequence([
                    RNAnimated.timing(val, {
                        toValue: 1,
                        duration: 400,
                        delay,
                        useNativeDriver: true,
                    }),
                    RNAnimated.timing(val, {
                        toValue: 0,
                        duration: 400,
                        useNativeDriver: true,
                    }),
                ]),
            )
        const a = animateDot(dot1, 0)
        const b = animateDot(dot2, 150)
        const c = animateDot(dot3, 300)
        a.start()
        b.start()
        c.start()
        return () => {
            a.stop()
            b.stop()
            c.stop()
        }
    }, [])

    const renderDot = (val) => (
        <RNAnimated.View
            style={[
                styles.typingDot,
                {
                    opacity: val.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0.3, 1],
                    }),
                    transform: [
                        {
                            translateY: val.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, -3],
                            }),
                        },
                    ],
                },
            ]}
        />
    )

    return (
        <View style={styles.typingDotsRow}>
            {renderDot(dot1)}
            {renderDot(dot2)}
            {renderDot(dot3)}
        </View>
    )
}

/** Ligne de squelette qui respire — dessine la réponse à venir. */
const SkeletonLine = ({ width }) => {
    const pulse = useRef(new RNAnimated.Value(0)).current

    useEffect(() => {
        const loop = RNAnimated.loop(
            RNAnimated.sequence([
                RNAnimated.timing(pulse, {
                    toValue: 1,
                    duration: 700,
                    easing: Easing.inOut(Easing.quad),
                    useNativeDriver: true,
                }),
                RNAnimated.timing(pulse, {
                    toValue: 0,
                    duration: 700,
                    easing: Easing.inOut(Easing.quad),
                    useNativeDriver: true,
                }),
            ]),
        )
        loop.start()
        return () => loop.stop()
    }, [])

    return (
        <RNAnimated.View
            style={[
                styles.skeletonLine,
                {
                    width,
                    opacity: pulse.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0.35, 0.8],
                    }),
                },
            ]}
        />
    )
}

/**
 * Carte d'attente pendant l'analyse d'une photo.
 *
 * L'analyse dure plusieurs secondes (téléversement + passe de vision). Le
 * seul retour était l'indicateur de frappe d'un chat — un signal calibré pour
 * deux secondes, qui ne dit ni ce qui se passe ni combien de temps ça prend :
 * de là l'impression de bug. On nomme l'étape, on dessine la réponse à venir,
 * et au-delà de 9 s on reconnaît l'attente.
 */
const AnalysisWaitCard = ({ label }) => {
    const [reassure, setReassure] = useState(false)

    useEffect(() => {
        setReassure(false)
        const t = setTimeout(() => setReassure(true), ANALYSIS_REASSURE_AFTER_MS)
        return () => clearTimeout(t)
    }, [label])

    return (
        <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(150)}
            style={[styles.row, { justifyContent: "flex-start" }]}
        >
            <View style={[styles.bubble, styles.bubbleSam, styles.analysisCard]}>
                <View style={styles.analysisHeaderRow}>
                    <FontAwesome5
                        name="eye"
                        size={11}
                        color={theme.colors.sam}
                        solid
                        style={{ marginRight: 7 }}
                    />
                    <TextM
                        fontWeight="Bold"
                        fontSize={12.5}
                        color={theme.colors.sam}
                        wrap
                        style={{ flexShrink: 1 }}
                    >
                        {label}
                    </TextM>
                    <View style={{ marginLeft: 8 }}>
                        <TypingDots />
                    </View>
                </View>
                <View style={styles.skeletonWrap}>
                    <SkeletonLine width="92%" />
                    <SkeletonLine width="76%" />
                    <SkeletonLine width="54%" />
                </View>
                {reassure ? (
                    <TextM
                        fontSize={11}
                        color={theme.colors.grey}
                        fontWeight="Medium"
                        wrap
                        style={{ marginTop: 8 }}
                    >
                        C'est un peu plus long que d'habitude — je regarde
                        encore, ne quitte pas.
                    </TextM>
                ) : null}
            </View>
        </Animated.View>
    )
}

/**
 * Une pastille d'action. Jamais démontée : `disabled` grise, mais la place
 * reste tenue — sinon le bas de la feuille se contracte dès que Sam commence
 * à réfléchir, puis se rouvre à la réponse, et la mise en page saute sous le
 * doigt à chaque tour.
 */
const ActionChip = ({ action, emphasis = false, disabled }) => {
    const isOff = disabled || action.disabled
    return (
        <TouchableOpacity
            onPress={action.onPress}
            disabled={isOff}
            activeOpacity={0.75}
            accessibilityRole="button"
            accessibilityLabel={action.label}
            style={[
                styles.chip,
                emphasis ? styles.chipEmphasis : styles.chipSoft,
                isOff && styles.chipDisabled,
            ]}
        >
            {action.icon ? (
                <FontAwesome5
                    name={action.icon}
                    size={11}
                    color={emphasis ? theme.colors.white : theme.colors.sam}
                    style={{ marginRight: 6 }}
                    solid
                />
            ) : null}
            <TextM
                fontWeight={emphasis ? "ExtraBold" : "Bold"}
                fontSize={12}
                color={emphasis ? theme.colors.white : theme.colors.sam}
            >
                {action.label}
            </TextM>
        </TouchableOpacity>
    )
}

/**
 * Carte de démarrage : une phrase entière, à la première personne, que
 * l'élève n'a plus qu'à choisir. Elle occupe l'espace vide de la feuille au
 * lieu de le laisser vide, et elle évite l'obstacle numéro un quand on est
 * bloqué — formuler sa question.
 *
 * Référence : la grille de Mindvalley, les cartes de Lovi et de ChatGPT. Une
 * pastille de 12 px ne remplit pas cet office : elle sert à relancer une
 * conversation en cours, pas à l'ouvrir.
 */
const StarterCard = ({ action, disabled }) => (
    <TouchableOpacity
        onPress={action.onPress}
        disabled={disabled || action.disabled}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel={action.starterLabel || action.label}
        style={[
            styles.starterCard,
            (disabled || action.disabled) && styles.starterCardDisabled,
        ]}
    >
        {action.icon ? (
            <FontAwesome5
                name={action.icon}
                size={13}
                color={theme.colors.sam}
                solid
                style={{ marginBottom: 5 }}
            />
        ) : null}
        <TextM
            fontWeight="SemiBold"
            fontSize={13}
            color={theme.colors.dark}
            wrap
            style={{ lineHeight: 18 }}
        >
            {action.starterLabel || action.label}
        </TextM>
    </TouchableOpacity>
)

// Le style du texte d'une bulle, fixe : un objet neuf à chaque rendu déjouait
// la mémoïsation de `RichText`.
const BUBBLE_TEXT_STYLE = { lineHeight: 20 }

/**
 * Le composeur. Le brouillon s'y tape : gardé en état dans la feuille, chaque
 * caractère re-rendait tout le fil — et, sur web, le Markdown et le KaTeX de
 * chaque bulle. Mais le composeur se démonte avec la feuille fermée : il repart
 * de `draftRef`, que la feuille garde (elle reste montée), pour que le message
 * en cours survive à l'aller-retour vers l'énoncé.
 */
const Composer = React.memo(function Composer({ onSend, disabled, placeholder, draftRef }) {
    const inputRef = useRef(null)
    const [draft, setDraftState] = useState(draftRef.current)
    const setDraft = (text) => {
        draftRef.current = text
        setDraftState(text)
    }
    const canSend = !!draft.trim() && !disabled

    const handleSend = () => {
        const text = draft.trim()
        if (!text || disabled) return
        onSend?.(text)
        setDraft("")
        // Rendre le focus au champ. Sans ça, l'élève doit viser à nouveau une
        // zone de 40 px avant de pouvoir répondre — quinze fois dans une
        // conversation de quinze tours. C'est la friction la plus coûteuse de
        // l'écran, et elle frappe à chaque échange.
        inputRef.current?.focus?.()
    }

    // Sur ordinateur, Entrée envoie et Maj+Entrée va à la ligne, comme chez les
    // assistants de référence. Le champ est multiligne et garde le focus à
    // l'envoi : sans ça, react-native-web y ajoutait une ligne au lieu
    // d'envoyer. Une saisie en cours de composition (accents, IME) n'envoie pas.
    const handleKeyPress = (e) => {
        const key = e?.nativeEvent ?? {}
        if (!IS_WEB || key.key !== "Enter" || key.shiftKey || key.isComposing || key.keyCode === 229) return
        e.preventDefault?.()
        handleSend()
    }

    // Une carte, champ + envoi — comme chez les assistants de référence, où
    // l'envoi est un rond en accent DANS la carte.
    return (
        <View style={styles.footer}>
            <View style={styles.composer}>
                <TextInput
                    ref={inputRef}
                    value={draft}
                    onChangeText={setDraft}
                    placeholder={placeholder}
                    placeholderTextColor={theme.colors.grey}
                    style={[
                        styles.input,
                        disabled && styles.inputDisabled,
                    ]}
                    editable={!disabled}
                    // Le backend refuse au-delà de 2000 caractères
                    // (SamConversationController). Sans cette borne, un élève
                    // qui collait un texte long déclenchait une 422 de
                    // validation — et le front la lisait comme une panne de
                    // Sam. Mieux vaut l'empêcher que l'expliquer.
                    maxLength={2000}
                    multiline
                    // Pas de `blurOnSubmit` : le clavier doit rester ouvert
                    // d'un tour à l'autre (cf. handleSend).
                    blurOnSubmit={false}
                    onSubmitEditing={handleSend}
                    onKeyPress={handleKeyPress}
                    returnKeyType="send"
                    accessibilityLabel="Écrire à Sam"
                />
                <View style={styles.composerActions}>
                    <TouchableOpacity
                        onPress={handleSend}
                        disabled={!canSend}
                        style={[
                            styles.sendBtn,
                            !canSend && styles.sendBtnDisabled,
                        ]}
                        activeOpacity={0.8}
                        accessibilityRole="button"
                        accessibilityLabel="Envoyer"
                    >
                        <FontAwesome5
                            name="arrow-up"
                            size={15}
                            color={theme.colors.white}
                        />
                    </TouchableOpacity>
                </View>
            </View>
        </View>
    )
})

const SamConversationSheet = ({
    visible,
    onClose,
    samMood,
    samNudgeKey,
    isSamThinking = false,
    chatHistory = [],
    onSendText,
    quickReplies = [],
    primaryAction = null,
    // { active, label } — analyse d'une photo en cours. Distinct de
    // `isSamThinking` : ici l'attente est longue et a des étapes, donc elle se
    // raconte au lieu de se réduire à trois points.
    analysis = null,
    capReached = false,
    // "activity" (Sam s'arrête sur cette activité) ou "daily" (jusqu'à demain).
    capScope = null,
    degraded = false,
}) => {
    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()
    const scrollRef = useRef(null)
    const dragY = useSharedValue(0)
    // Le message en cours, gardé tant que la séance est ouverte (cf. Composer).
    const draftRef = useRef("")

    const inputDisabled = capReached
    const analysisActive = !!analysis?.active
    const status = statusOf(isSamThinking)

    // État d'accueil : rien ne s'est encore passé. On l'affiche tant que
    // l'élève n'a pas parlé ET que Sam n'a pas dit plus d'une chose — au-delà,
    // c'est une conversation, et le fil reprend sa place.
    //
    // Quand Sam a déjà lancé sa relance d'ouverture, c'est ELLE qui sert de
    // sous-titre : autant faire porter l'accueil par sa propre voix que par
    // une phrase générique de plus.
    const samMessages = chatHistory.filter((m) => m.role === "sam")
    const openingOnly = samMessages.every(
        (m) => m.origin === SAM_BUBBLE_ORIGIN.proactive
    )
    const showIntro =
        !chatHistory.some((m) => m.role === "student") &&
        // Uniquement des bonjours : une réponse conservée en cache (« je
        // n'arrive pas à regarder ton travail ») se retrouvait sinon promue
        // en accroche de bienvenue.
        openingOnly &&
        samMessages.length <= 1 &&
        !analysisActive &&
        !isSamThinking
    // Texte propre à l'accueil, et NON le bonjour de Sam : celui-ci dit déjà
    // « Coucou, je suis Sam », si bien que l'écran se présentait deux fois.
    // Le bonjour, lui, garde son rôle dans l'aperçu quand la feuille est
    // fermée.
    const introLine = "Sur quoi tu bloques ?"
    // Les cartes de démarrage reprennent les MÊMES actions que les pastilles :
    // une seule source de vérité, deux présentations selon le moment.
    const starterActions = [
        ...(primaryAction ? [primaryAction] : []),
        ...quickReplies,
    ]
    // Symétrique : certaines entrées n'existent que pour ouvrir la
    // conversation et n'ont plus leur place dans la rangée compacte.
    const chipActions = quickReplies.filter((qr) => !qr.starterOnly)
    // Pendant l'analyse d'une photo, la carte d'attente porte déjà le signal :
    // deux animations concurrentes pour un seul événement, c'est du bruit.
    const showTypingBubble = isSamThinking && !analysisActive
    const actionsDisabled = capReached || isSamThinking

    const screenH = dimensions?.height || 800
    const useSidePanel = isWebSidePanel(dimensions?.width)
    const defaultH = Math.round(screenH * DETENT_RATIO.default)
    const largeH = Math.round(screenH * DETENT_RATIO.large)
    const [detent, setDetent] = useState(DETENT.default)
    // Le clavier logiciel recouvrait le composeur : la feuille est ancrée en
    // bas avec une hauteur fixe, donc elle ne bougeait pas quand il s'ouvrait,
    // et l'élève écrivait sans voir ce qu'il tapait. Une surface en position
    // absolue ne peut pas s'en remettre à `KeyboardAvoidingView` — elle n'est
    // pas dans le flux. Elle doit donc se replier elle-même.
    // Sur Android la feuille ne remonte pas (cf. useKeyboardLift) : elle se
    // borne à la place qui reste au-dessus du clavier (`maxHeight` plus bas).
    const { open: keyboardOpen, lift: keyboardLift } = useKeyboardLift()
    // Hauteur dérivée de l'état, en style simple. Passer par une valeur
    // partagée animée ne fonctionne PAS ici : sur rn-web la hauteur inline
    // restait figée à sa valeur initiale, si bien que le cran changeait sans
    // que la feuille bouge (vérifié à l'écran). Le changement est donc net
    // plutôt que ressorti — franc, et surtout réel sur les deux plateformes.
    // Clavier ouvert = hauteur haute, quel que soit le cran choisi : sans ça,
    // le pied remonte au-dessus du clavier mais il ne reste qu'un quart
    // d'écran de conversation. C'est aussi ce que font les feuilles iOS,
    // qui s'agrandissent à la mise au point d'un champ.
    const sheetH = detent === DETENT.large || keyboardOpen ? largeH : defaultH
    // Miroir du cran pour le worklet du geste, qui ne peut pas lire l'état
    // React. Écrit ici, lu là-bas.
    const isLargeSV = useSharedValue(false)
    useEffect(() => {
        isLargeSV.value = detent === DETENT.large
    }, [detent])

    useEffect(() => {
        // L'accueil se lit d'en haut : descendre au bout, comme pour un fil,
        // coupait la tête de Sam dès qu'il dépassait de la feuille.
        if (!visible || showIntro) return
        const t = setTimeout(() => {
            scrollRef.current?.scrollToEnd?.({ animated: true })
        }, 80)
        return () => clearTimeout(t)
    }, [chatHistory.length, isSamThinking, analysisActive, keyboardOpen, visible, showIntro])

    // Le geste ne fait que DÉCIDER du cran ; la hauteur, elle, est rendue par
    // React. Vers le bas, la feuille suit le doigt pour donner la sensation de
    // prise, puis revient à zéro.
    const dragStartY = useSharedValue(0)
    const dragFromLarge = useSharedValue(false)
    const panGesture = Gesture.Pan()
        .onStart(() => {
            dragStartY.value = dragY.value
            dragFromLarge.value = isLargeSV.value
        })
        .onUpdate((e) => {
            if (e.translationY > 0) dragY.value = dragStartY.value + e.translationY
        })
        .onEnd((e) => {
            if (e.translationY < -40) {
                runOnJS(setDetent)(DETENT.large)
            } else if (e.translationY > 100 || e.velocityY > 700) {
                if (dragFromLarge.value) {
                    // Depuis la hauteur haute, on redescend d'un cran : le
                    // premier geste vers le bas sert à revoir l'énoncé, pas à
                    // quitter la conversation.
                    runOnJS(setDetent)(DETENT.default)
                } else {
                    runOnJS(onClose)()
                }
            }
            dragY.value = withSpring(0)
        })

    const dragStyle = useAnimatedStyle(() => ({
        transform: [{ translateY: dragY.value }],
    }))

    if (!visible) return null

    // Une salve = des messages consécutifs du même interlocuteur. Seule la
    // dernière porte la queue, et l'espacement se resserre à l'intérieur.
    const speakerOf = (msg) => (msg?.role === "sam" ? "sam" : "student")

    const renderMessage = (msg, index) => {
        const isLastOfGroup =
            speakerOf(chatHistory[index + 1]) !== speakerOf(msg)
        const isFirstOfGroup =
            speakerOf(chatHistory[index - 1]) !== speakerOf(msg)
        const isSam = msg.role === "sam"
        const isStudent = msg.role === "student"
        const isScan = msg.kind === "scan"

        if (isStudent && isScan) {
            // La photo que l'élève vient d'envoyer, côté élève. Sans elle,
            // le seul retour est du texte : rien ne montre que le cliché est
            // bien parti — et en mode dégradé (quali indisponible) il n'y a
            // même plus de texte utile. `imageUri` est l'URI locale du
            // cliché, donc la bulle s'affiche sans dépendre du serveur.
            if (!msg.imageUri) return null
            return (
                <View
                    key={msg.id}
                    style={[styles.row, { justifyContent: "flex-end" }]}
                >
                    <View style={styles.scanBubble}>
                        <Image
                            source={{ uri: msg.imageUri }}
                            style={styles.scanImage}
                            resizeMode="cover"
                            // L'image grandit le contenu APRÈS que l'effet
                            // d'auto-défilement a joué : sans ce rappel, la
                            // bulle la plus récente arrive tronquée en haut.
                            onLoad={() =>
                                scrollRef.current?.scrollToEnd?.({
                                    animated: true,
                                })
                            }
                        />
                    </View>
                </View>
            )
        }

        if (isSam) {
            // Pas d'avatar dans le fil : dans un dialogue à deux, il n'apporte
            // aucune information et coûte 40 px de largeur utile par message,
            // sur l'écran où la largeur est la ressource rare (cf. WhatsApp).
            return (
                <View
                    key={msg.id}
                    style={[
                        styles.row,
                        { justifyContent: "flex-start" },
                        !isFirstOfGroup && styles.rowInGroup,
                    ]}
                >
                    <View
                        style={[
                            styles.bubble,
                            styles.bubbleSam,
                            isLastOfGroup && styles.bubbleSamTail,
                        ]}
                    >
                        <RichText
                            content={msg.content || ""}
                            fontSize={14}
                            color={theme.colors.dark}
                            style={BUBBLE_TEXT_STYLE}
                        />
                    </View>
                </View>
            )
        }

        // Student normal message
        // Un message sans texte n'a rien à montrer : le rendre produisait une
        // pastille bleue vide dans le fil (relevée en recette sur téléphone).
        // Mieux vaut ne rien afficher qu'une bulle qui ne dit rien.
        if (!msg.content) return null
        return (
            <View
                key={msg.id}
                style={[
                    styles.row,
                    { justifyContent: "flex-end" },
                    !isFirstOfGroup && styles.rowInGroup,
                ]}
            >
                <View
                    style={[
                        styles.bubble,
                        styles.bubbleStudent,
                        isLastOfGroup && styles.bubbleStudentTail,
                    ]}
                >
                    <TextM
                        fontSize={14}
                        color={theme.colors.white}
                        wrap
                        style={{ lineHeight: 20 }}
                    >
                        {msg.content}
                    </TextM>
                </View>
            </View>
        )
    }

    return (
        <Animated.View
            entering={
                useSidePanel
                    ? SlideInRight.duration(280)
                    : SlideInDown.duration(320)
            }
            exiting={
                useSidePanel
                    ? SlideOutRight.duration(220)
                    : SlideOutDown.duration(240)
            }
            style={[
                styles.sheet,
                useSidePanel
                    ? styles.sheetSidePanel
                    : [
                          styles.sheetBottom,
                          dragStyle,
                          {
                              height: sheetH + insets.bottom,
                              maxHeight: "100%",
                              // Le clavier couvre déjà la zone sûre du bas :
                              // ajouter les deux laisserait un vide sous le
                              // composeur.
                              paddingBottom: keyboardLift || insets.bottom,
                          },
                      ],
            ]}
        >
            {!useSidePanel && (
                <GestureDetector gesture={panGesture}>
                    <Animated.View>
                        <TouchableOpacity
                            onPress={() =>
                                setDetent(
                                    detent === DETENT.large
                                        ? DETENT.default
                                        : DETENT.large
                                )
                            }
                            activeOpacity={0.6}
                            // Le geste reste le chemin principal ; le tap le
                            // double pour qui ne pense pas à glisser, et donne
                            // une cible au lecteur d'écran.
                            accessibilityRole="button"
                            accessibilityLabel={
                                detent === DETENT.large
                                    ? "Réduire la conversation pour revoir l'énoncé"
                                    : "Agrandir la conversation"
                            }
                            style={styles.handleZone}
                        >
                            <View style={styles.handleBar} />
                        </TouchableOpacity>
                    </Animated.View>
                </GestureDetector>
            )}

            {/* En-tête : une seule ligne. Et rien du tout pendant l'accueil,
              * où le grand Sam juste en dessous porte déjà l'identité — le
              * répéter ici, c'était refaire le doublon qu'on vient d'enlever. */}
            <View style={styles.header}>
                {showIntro ? null : (
                    <>
                        <SamHaloAvatar
                            samMood={samMood}
                            samNudgeKey={samNudgeKey}
                            active={isSamThinking}
                        />
                        <View style={styles.headerText}>
                            <TextM
                                fontWeight="ExtraBold"
                                fontSize={15}
                                color={theme.colors.dark}
                            >
                                Sam
                            </TextM>
                            {status ? (
                                <TextM
                                    fontSize={12}
                                    color={theme.colors.sam}
                                    fontWeight="SemiBold"
                                >
                                    {status}
                                </TextM>
                            ) : null}
                        </View>
                    </>
                )}
                {showIntro ? <View style={{ flex: 1 }} /> : null}
                <TouchableOpacity
                    onPress={onClose}
                    style={styles.closeBtn}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Fermer la conversation"
                >
                    <FontAwesome5
                        name="times"
                        size={18}
                        color={theme.colors.grey}
                    />
                </TouchableOpacity>
            </View>

            {/* Fil de la conversation */}
            <ScrollView
                ref={scrollRef}
                style={styles.scroll}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
            >
                {showIntro ? (
                    <View style={styles.introWrap}>
                        <View style={styles.introAvatar}>
                            <ChampionImageM
                                name="sam"
                                animated
                                animationKey={`${samMood}-intro`}
                                width={INTRO_AVATAR_SIZE - 8}
                            />
                        </View>
                        <TextM
                            fontWeight="ExtraBold"
                            fontSize={21}
                            color={theme.colors.dark}
                            wrap
                            style={{ textAlign: "center", marginTop: 8 }}
                        >
                            Salut, moi c'est Sam.
                        </TextM>
                        <TextM
                            fontWeight="Medium"
                            fontSize={14}
                            color={theme.colors.grey}
                            wrap
                            style={{
                                textAlign: "center",
                                marginTop: 6,
                                lineHeight: 20,
                                maxWidth: 300,
                            }}
                        >
                            {introLine}
                        </TextM>
                        {starterActions.length > 0 ? (
                            <View style={styles.starterGrid}>
                                {starterActions.map((action) => (
                                    <StarterCard
                                        key={action.key || action.label}
                                        action={action}
                                        disabled={capReached}
                                    />
                                ))}
                            </View>
                        ) : null}
                    </View>
                ) : (
                    chatHistory.map(renderMessage)
                )}
                {analysisActive && (
                    <AnalysisWaitCard
                        label={analysis.label || "Je regarde ton travail…"}
                    />
                )}
                {showTypingBubble && (
                    <Animated.View
                        entering={FadeIn.duration(200)}
                        exiting={FadeOut.duration(150)}
                        style={[styles.row, { justifyContent: "flex-start" }]}
                    >
                        <View style={[styles.bubble, styles.bubbleSam]}>
                            <TypingDots />
                        </View>
                    </Animated.View>
                )}
            </ScrollView>

            {/* Bandeaux d'état (cap atteint / mode dégradé) */}
            {capReached && (
                <View style={[styles.stateBanner, styles.stateBannerCap]}>
                    <FontAwesome5
                        name="hourglass-end"
                        size={12}
                        color={theme.colors.error}
                        style={{ marginRight: 6 }}
                    />
                    <TextM
                        fontWeight="SemiBold"
                        fontSize={12}
                        color={theme.colors.error}
                        wrap
                    >
                        {capScope === "daily"
                            ? "Sam a beaucoup travaillé avec toi aujourd'hui — il revient demain. Tu peux finir l'activité sans lui."
                            : "Sam s'est beaucoup exprimé sur cette activité — termine-la, il revient pour la prochaine."}
                    </TextM>
                </View>
            )}
            {!capReached && degraded && (
                <View style={[styles.stateBanner, styles.stateBannerDegraded]}>
                    <FontAwesome5
                        name="wifi"
                        size={12}
                        color={theme.colors.warning}
                        style={{ marginRight: 6 }}
                    />
                    <TextM
                        fontWeight="SemiBold"
                        fontSize={12}
                        color={theme.colors.warning}
                        wrap
                    >
                        Sam met du temps à répondre. Tu peux continuer
                        l'activité sans lui.
                    </TextM>
                </View>
            )}

            {/* Une seule rangée d'actions : la principale en pastille pleine,
              * les intentions conversationnelles en pastilles teintées. Elles
              * portent leur sens dans leur libellé — « Regarde mon avancement »
              * ne peut pas se confondre avec terminer l'activité. */}
            {!showIntro && (primaryAction || chipActions.length > 0) && (
                <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={styles.actionsScroll}
                    contentContainerStyle={styles.actionsContent}
                    keyboardShouldPersistTaps="always"
                >
                    {primaryAction ? (
                        <ActionChip
                            key={primaryAction.key || primaryAction.label}
                            action={primaryAction}
                            emphasis
                            disabled={actionsDisabled}
                        />
                    ) : null}
                    {chipActions.map((qr) => (
                        <ActionChip
                            key={qr.key || qr.label}
                            action={qr}
                            disabled={actionsDisabled}
                        />
                    ))}
                </ScrollView>
            )}

            <Composer
                onSend={onSendText}
                draftRef={draftRef}
                disabled={inputDisabled}
                placeholder={
                    capReached
                        ? capScope === "daily"
                            ? "Sam revient demain"
                            : "Cap de messages atteint pour cette activité"
                        : "Écris à Sam…"
                }
            />
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    sheet: {
        position: "absolute",
        // Très légèrement teinté, pas blanc : c'est ce qui permet aux bulles
        // pleines et au composeur blanc de se détacher du fond.
        backgroundColor: SHEET_GROUND,
        zIndex: 25,
    },
    sheetBottom: {
        left: 0,
        right: 0,
        bottom: 0,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        shadowColor: theme.colors.black,
        shadowOffset: { width: 0, height: -6 },
        shadowOpacity: 0.18,
        shadowRadius: 20,
        elevation: 20,
    },
    sheetSidePanel: {
        right: 0,
        top: 0,
        bottom: 0,
        width: WEB_SIDE_PANEL_WIDTH,
        borderTopLeftRadius: 16,
        borderBottomLeftRadius: 16,
        shadowColor: theme.colors.black,
        shadowOffset: { width: -6, height: 0 },
        shadowOpacity: 0.14,
        shadowRadius: 18,
        elevation: 16,
    },
    handleZone: {
        // Zone de préhension généreuse : la barre ne fait que 5 px de haut,
        // c'est la surface autour qui rend le geste attrapable.
        alignItems: "center",
        justifyContent: "center",
        paddingTop: 8,
        paddingBottom: 6,
    },
    handleBar: {
        width: 44,
        height: 5,
        borderRadius: 3,
        backgroundColor: theme.colors.lightMore,
    },
    // En-tête
    header: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 14,
        paddingTop: 6,
        paddingBottom: 8,
    },
    haloWrap: {
        width: SAM_AVATAR_SIZE,
        height: SAM_AVATAR_SIZE,
        alignItems: "center",
        justifyContent: "center",
    },
    halo: {
        position: "absolute",
        width: SAM_AVATAR_SIZE,
        height: SAM_AVATAR_SIZE,
        borderRadius: SAM_AVATAR_SIZE / 2,
    },
    avatarCircle: {
        width: SAM_AVATAR_SIZE,
        height: SAM_AVATAR_SIZE,
        borderRadius: SAM_AVATAR_SIZE / 2,
        backgroundColor: theme.colors.white,
        borderWidth: 2,
        borderColor: theme.colors.sam,
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
    },
    headerText: {
        flex: 1,
        marginLeft: 10,
        // Hauteur fixée : le statut n'apparaît que pendant l'attente, et sans
        // cette réserve l'en-tête grandissait puis rétrécissait à chaque tour
        // de parole — exactement le sursaut qu'on a enlevé plus bas.
        height: 38,
        justifyContent: "center",
    },
    closeBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.colors.lightMore + "70",
    },
    // Fil
    scroll: {
        flex: 1,
    },
    scrollContent: {
        padding: 14,
        gap: 8,
        // Pour que l'état d'accueil puisse se centrer verticalement dans la
        // hauteur disponible. Sans effet dès que le fil dépasse l'écran.
        flexGrow: 1,
    },
    rowInGroup: {
        marginTop: -4,
    },
    row: {
        flexDirection: "row",
        alignItems: "flex-end",
        marginVertical: 2,
        // Without an explicit width, the row collapses to its
        // natural content width on rn-web, breaking the bubble's
        // maxWidth wrap. Pin it to the parent's available width.
        width: "100%",
    },
    bubble: {
        // The bubble must shrink against its parent row, otherwise
        // long replies overflow the sheet on narrow viewports. Cap it
        // to 85% of the available wrapper width and keep it content-
        // sized via alignSelf so it doesn't get stretched out by the
        // wrapper's flex layout.
        alignSelf: "flex-start",
        maxWidth: "85%",
        flexShrink: 1,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 18,
    },
    bubbleSam: {
        // PLEINE, sans bordure. Une bulle blanche cerclée d'un trait d'un
        // pixel, posée sur du blanc, ne se lit pas comme une bulle : la
        // conversation devenait une liste plate de rectangles. Aucune des
        // références vérifiées (iMessage, WhatsApp, Glovo, Badoo, GoHenry) ne
        // fait autrement — le côté reçu est toujours un aplat.
        backgroundColor: theme.colors.white,
    },
    bubbleStudent: {
        backgroundColor: theme.colors.primary,
    },
    // Coin « queue » sur la DERNIÈRE bulle d'une salve seulement, comme
    // iMessage : deux messages de suite se lisent alors comme un seul tour de
    // parole, au lieu de deux blocs sans lien.
    bubbleSamTail: {
        borderBottomLeftRadius: 6,
    },
    bubbleStudentTail: {
        borderBottomRightRadius: 6,
    },
    analysisCard: {
        // La carte d'attente n'est pas une bulle de parole : elle occupe une
        // largeur stable pour que les squelettes dessinent bien la réponse.
        maxWidth: "92%",
        minWidth: 220,
        backgroundColor: theme.colors.sam + "0C",
        borderColor: theme.colors.sam + "33",
    },
    analysisHeaderRow: {
        flexDirection: "row",
        alignItems: "center",
    },
    skeletonWrap: {
        marginTop: 10,
        gap: 6,
    },
    skeletonLine: {
        height: 8,
        borderRadius: 4,
        backgroundColor: theme.colors.sam + "40",
    },
    scanBubble: {
        alignSelf: "flex-end",
        maxWidth: "70%",
        borderRadius: 16,
        borderTopRightRadius: 4,
        overflow: "hidden",
        backgroundColor: theme.colors.primary,
        padding: 3,
    },
    scanImage: {
        width: 150,
        height: 200,
        borderRadius: 14,
        borderTopRightRadius: 2,
    },
    typingDotsRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
    },
    typingDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: theme.colors.grey,
    },
    // Bandeaux d'état
    stateBanner: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore + "60",
    },
    stateBannerCap: {
        backgroundColor: theme.colors.error + "10",
    },
    stateBannerDegraded: {
        backgroundColor: theme.colors.warning + "12",
    },
    // Accueil
    introWrap: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 4,
    },
    introAvatar: {
        width: INTRO_AVATAR_SIZE,
        height: INTRO_AVATAR_SIZE,
        borderRadius: INTRO_AVATAR_SIZE / 2,
        backgroundColor: theme.colors.white,
        borderWidth: 3,
        borderColor: theme.colors.sam + "40",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
    },
    starterGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 8,
        marginTop: 12,
        width: "100%",
    },
    starterCard: {
        // Deux par ligne : la grille remplit le vide de la feuille au lieu de
        // le laisser béant, et une phrase entière tient sur deux lignes.
        flexBasis: "47%",
        flexGrow: 1,
        minWidth: 140,
        maxWidth: 220,
        backgroundColor: theme.colors.white,
        borderRadius: 14,
        paddingHorizontal: 13,
        paddingVertical: 9,
        borderWidth: 1,
        borderColor: theme.colors.sam + "1F",
    },
    starterCardDisabled: {
        opacity: 0.45,
    },
    // Actions
    actionsScroll: {
        flexGrow: 0,
    },
    actionsContent: {
        paddingHorizontal: 12,
        paddingTop: 8,
        paddingBottom: 2,
        gap: 7,
        alignItems: "center",
    },
    chip: {
        flexDirection: "row",
        alignItems: "center",
        // Serré à dessein : la rangée doit tenir sur un écran de 390 px avec
        // trois pastilles, sinon la dernière sort du cadre.
        paddingHorizontal: 11,
        paddingVertical: 8,
        borderRadius: 18,
    },
    chipSoft: {
        // Pleine, pas un contour à 33 % d'opacité sur du blanc : « Indice » et
        // « Rappel de cours » sont les deux entrées qui évitent à l'élève
        // d'avoir à formuler sa question — c'est-à-dire l'obstacle numéro un
        // quand on est bloqué. L'aplat est blanc et non teinté : le fond de la
        // feuille l'est déjà, une teinte sur une teinte ne se voit plus.
        backgroundColor: theme.colors.white,
    },
    chipEmphasis: {
        backgroundColor: theme.colors.sam,
    },
    chipDisabled: {
        opacity: 0.45,
    },
    // Composeur
    footer: {
        paddingHorizontal: 12,
        paddingTop: 6,
        paddingBottom: 10,
    },
    composer: {
        // Blanc sur le fond teinté, légèrement surélevé : le composeur est
        // une surface d'écriture, il doit se lire comme telle plutôt que se
        // fondre en gris sur gris.
        backgroundColor: theme.colors.white,
        borderRadius: 22,
        paddingHorizontal: 6,
        paddingTop: 4,
        paddingBottom: 6,
        shadowColor: theme.colors.black,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
    },
    input: {
        minHeight: 36,
        maxHeight: 110,
        paddingHorizontal: 10,
        paddingTop: 8,
        paddingBottom: 4,
        fontSize: 14,
        color: theme.colors.dark,
        fontFamily: "Montserrat-Regular",
        // rn-web dessine un contour bleu par défaut sur un champ multiligne
        // dans une carte : le focus est déjà porté par la bordure de la carte.
        ...(IS_WEB ? { outlineStyle: "none" } : {}),
    },
    inputDisabled: {
        opacity: 0.5,
    },
    composerActions: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 4,
        paddingRight: 2,
    },
    sendBtn: {
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: theme.colors.sam,
        alignItems: "center",
        justifyContent: "center",
    },
    sendBtnDisabled: {
        backgroundColor: theme.colors.lightMore,
    },
})

export default SamConversationSheet
